import axios from 'axios';

const getApiUrl = () => {
  const baseUrl = process.env.REACT_APP_BACKEND_URL;
  return baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`;
};
const API = getApiUrl();

const authHeaders = () => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('authToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const ConstructionAPI = {
  getSectors: () => axios.get(`${API}/construction/sectors`).then(r => r.data),
  getPhases:  () => axios.get(`${API}/construction/phases`).then(r => r.data),
  getContracts: () => axios.get(`${API}/construction/contracts`).then(r => r.data),
  getCriticality: () => axios.get(`${API}/construction/criticality`).then(r => r.data),
  getRegions: () => axios.get(`${API}/construction/regions`).then(r => r.data),
  getCities: (region) => axios.get(`${API}/construction/cities`, { params: region ? { region } : {} }).then(r => r.data),
  getTaxPolicy: (region) => axios.get(`${API}/construction/tax-policy`, { params: region ? { region } : {} }).then(r => r.data),
  getMaterials: (region = 'us') => axios.get(`${API}/construction/rates/materials`, { params: { region } }).then(r => r.data),
  getLabour: (region = 'us') => axios.get(`${API}/construction/rates/labour`, { params: { region } }).then(r => r.data),
  getForex: () => axios.get(`${API}/construction/forex`).then(r => r.data),
  updateForex: (updates) => axios.put(`${API}/construction/forex`, updates, { headers: authHeaders() }).then(r => r.data),
  uploadRateSheet: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return axios.post(`${API}/construction/rates/upload`, fd, { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  calculate: (payload) => axios.post(`${API}/construction/calculate`, payload, { headers: authHeaders() }).then(r => r.data),
  saveProject: (calculation, notes) => axios.post(`${API}/construction/projects`, { calculation, notes }, { headers: authHeaders() }).then(r => r.data),
  listProjects: () => axios.get(`${API}/construction/projects`, { headers: authHeaders() }).then(r => r.data),
  deleteProject: (id) => axios.delete(`${API}/construction/projects/${id}`, { headers: authHeaders() }).then(r => r.data),

  // Resource / material / equipment plan derived from a calculation
  getPlan: (calculation) => axios.post(`${API}/construction/plan`, { calculation }, { headers: authHeaders() }).then(r => r.data),

  // Risk library + city-specific risks
  getRiskLibrary: (sector, city) => axios.get(`${API}/construction/risks/library`, {
    params: { ...(sector ? { sector } : {}), ...(city ? { city } : {}) }
  }).then(r => r.data),
  evaluateRisks: (payload) => axios.post(`${API}/construction/risks/evaluate`, payload, { headers: authHeaders() }).then(r => r.data),

  // Funding drawdown analysis
  analyzeFunding: (payload) => axios.post(`${API}/construction/funding/analyze`, payload, { headers: authHeaders() }).then(r => r.data),

  // Live market rates via LLM
  liveRefreshRates: (items, location) => axios.post(`${API}/construction/rates/live-refresh`, { items, location }, { headers: authHeaders() }).then(r => r.data),

  // Amenities catalog
  getAmenities: (sector) => axios.get(`${API}/construction/amenities`, { params: sector ? { sector } : {} }).then(r => r.data),

  // Drawing import (vision LLM)
  analyzeDrawing: (formData) => axios.post(`${API}/construction/drawings/analyze`, formData, {
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    timeout: 90000,
  }).then(r => r.data),

  // Cost agreements (collab) — votes per cost line
  addCostAgreement: (projectId, payload) => axios.post(`${API}/construction/projects/${projectId}/cost-agreements`, payload, { headers: authHeaders() }).then(r => r.data),
  removeCostAgreement: (projectId, id) => axios.delete(`${API}/construction/projects/${projectId}/cost-agreements/${id}`, { headers: authHeaders() }).then(r => r.data),

  // Baseline / Actuals / Variance
  setBaseline: (projectId, calculation = null) => axios.post(`${API}/construction/projects/${projectId}/baseline`, { calculation }, { headers: authHeaders() }).then(r => r.data),
  clearBaseline: (projectId) => axios.delete(`${API}/construction/projects/${projectId}/baseline`, { headers: authHeaders() }).then(r => r.data),
  addActual: (projectId, payload) => axios.post(`${API}/construction/projects/${projectId}/actuals`, payload, { headers: authHeaders() }).then(r => r.data),
  removeActual: (projectId, actualId) => axios.delete(`${API}/construction/projects/${projectId}/actuals/${actualId}`, { headers: authHeaders() }).then(r => r.data),
  getVariance: (projectId) => axios.get(`${API}/construction/projects/${projectId}/variance`, { headers: authHeaders() }).then(r => r.data),
  adviseVariance: (projectId) => axios.post(`${API}/construction/projects/${projectId}/variance/advise`, {}, { headers: authHeaders(), timeout: 90000 }).then(r => r.data),

  // Bulk CSV upload of actuals + revised-burn forecast
  uploadActualsCsv: (projectId, formData) => axios.post(`${API}/construction/projects/${projectId}/actuals/csv`, formData, {
    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  }).then(r => r.data),
  computeRevisedBurn: (projectId, payload) => axios.post(`${API}/construction/projects/${projectId}/revised-burn`, payload, { headers: authHeaders() }).then(r => r.data),

  // EVM
  computeEvm: (payload) => axios.post(`${API}/construction/evm/compute`, payload, { headers: authHeaders() }).then(r => r.data),

  // Budget narrative (LLM)
  generateNarrative: (calculation, notes) => axios.post(`${API}/construction/narrative`, { calculation, notes }, { headers: authHeaders() }).then(r => r.data),

  // Collaboration (per saved project)
  getCollab: (projectId) => axios.get(`${API}/construction/projects/${projectId}/collab`, { headers: authHeaders() }).then(r => r.data),
  inviteMember: (projectId, email, role = 'viewer') => axios.post(`${API}/construction/projects/${projectId}/members`, { email, role }, { headers: authHeaders() }).then(r => r.data),
  removeMember: (projectId, email) => axios.delete(`${API}/construction/projects/${projectId}/members/${encodeURIComponent(email)}`, { headers: authHeaders() }).then(r => r.data),
  addComment: (projectId, body, target_type = 'budget', target_id = 'root') => axios.post(`${API}/construction/projects/${projectId}/comments`, { body, target_type, target_id }, { headers: authHeaders() }).then(r => r.data),
  resolveComment: (projectId, commentId) => axios.post(`${API}/construction/projects/${projectId}/comments/${commentId}/resolve`, {}, { headers: authHeaders() }).then(r => r.data),
  addApproval: (projectId, decision, note) => axios.post(`${API}/construction/projects/${projectId}/approvals`, { decision, note }, { headers: authHeaders() }).then(r => r.data),

  // WBS + equipment library
  getWbs: (phaseKey) => axios.get(`${API}/construction/wbs${phaseKey ? `/${phaseKey}` : ''}`).then(r => r.data),
  getEquipment: (region = 'us') => axios.get(`${API}/construction/equipment`, { params: { region } }).then(r => r.data),
};

export default ConstructionAPI;
